import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API_BASE } from "@/lib/api";
import { Loader2, ExternalLink, Download, AlertCircle, FileText, Youtube } from "lucide-react";

/**
 * Inline-preview för material (PDF, .docx, bilder, text).
 * Hämtar /api/materials/{id}/preview och renderar enligt `kind`.
 */
export function MaterialPreviewModal({ materialId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !materialId) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    api.get(`/materials/${materialId}/preview`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Kunde inte ladda preview"))
      .finally(() => setLoading(false));
  }, [materialId, open]);

  const token = (typeof window !== "undefined" && localStorage.getItem("grindstugatan_token")) || "";
  const base = API_BASE || "";
  const absFileUrl = data?.file_url ? `${base}${data.file_url}?token=${encodeURIComponent(token)}` : null;

  // YouTube-modal vill ha mer bredd för läsbar 16:9-yta
  const isYoutube = data?.kind === "youtube";
  const externalUrl = isYoutube ? data?.source_url : absFileUrl;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={`${isYoutube ? "max-w-5xl" : "max-w-3xl"} w-[95vw] max-h-[92vh] p-0 overflow-hidden flex flex-col z-[60]`}
        data-testid="material-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-[color:var(--border-subtle)]">
          <div className="flex items-center justify-between gap-2 pr-8">
            <DialogTitle className="font-display text-sm truncate" title={data?.filename || ""}>
              {isYoutube ? (
                <Youtube size={14} className="inline mr-1.5 -mt-0.5 text-[#DC2626]" />
              ) : (
                <FileText size={14} className="inline mr-1.5 -mt-0.5 text-[color:var(--jarvis)]" />
              )}
              {data?.filename || "Förhandsvisning"}
            </DialogTitle>
            {externalUrl && (
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] inline-flex items-center gap-1 text-[color:var(--text-secondary)] hover:text-[color:var(--jarvis)] shrink-0"
                data-testid="material-open-external"
              >
                <ExternalLink size={11} /> Öppna externt
              </a>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-[color:var(--bg-canvas)]">
          {loading && (
            <div className="flex items-center justify-center p-12 text-sm text-[color:var(--text-tertiary)]">
              <Loader2 size={16} className="animate-spin mr-2" /> Laddar förhandsvisning…
            </div>
          )}
          {error && (
            <div className="p-6 text-sm text-[color:var(--danger)] flex items-start gap-2">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && data?.kind === "youtube" && data?.embed_url && (
            <div className="bg-black w-full" data-testid="material-preview-youtube">
              {/* 16:9 responsiv container */}
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  src={`${data.embed_url}&autoplay=1`}
                  title={data.filename || "YouTube-video"}
                  className="absolute inset-0 w-full h-full"
                  style={{ border: "none" }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {!loading && !error && data?.kind === "pdf" && absFileUrl && (
            <iframe
              src={absFileUrl}
              title={data.filename}
              className="w-full"
              style={{ height: "78vh", border: "none" }}
              data-testid="material-preview-pdf"
            />
          )}

          {!loading && !error && data?.kind === "image" && absFileUrl && (
            <div className="p-3 flex items-center justify-center">
              <img
                src={absFileUrl}
                alt={data.filename}
                className="max-w-full max-h-[78vh] object-contain rounded"
                data-testid="material-preview-image"
              />
            </div>
          )}

          {!loading && !error && data?.kind === "docx" && (
            <div
              className="docx-preview p-6 bg-white max-w-[800px] mx-auto my-3 rounded shadow-sm prose prose-sm prose-stone"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.html || "", { ADD_ATTR: ["target"] }) }}
              data-testid="material-preview-docx"
            />
          )}

          {!loading && !error && data?.kind === "text" && (
            <div>
              {data.note && (
                <p className="mx-4 mt-3 px-3 py-2 text-xs rounded-lg bg-[#FFF7EC] text-[#9A6B2F] border border-[#F0DFC0]"
                   data-testid="material-preview-degraded-note">
                  {data.note}
                </p>
              )}
              <pre
                className="p-4 text-xs whitespace-pre-wrap font-mono leading-relaxed"
                data-testid="material-preview-text"
              >
                {data.text}
              </pre>
            </div>
          )}

          {!loading && !error && data?.kind === "unsupported" && (
            <div className="p-8 text-center text-sm text-[color:var(--text-secondary)]">
              <FileText size={32} className="mx-auto text-[color:var(--text-tertiary)] mb-2" />
              <p>Den här filtypen kan inte förhandsvisas inline.</p>
              {absFileUrl && (
                <a
                  href={absFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 px-3 py-2 rounded-full bg-[color:var(--jarvis)] text-white text-xs"
                  data-testid="material-download-fallback"
                >
                  <Download size={12} /> Ladda ner och öppna
                </a>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
